import React from 'react';
import { Eye, Route, Users2, Compass, Bell, TrendingUp, ShieldCheck } from 'lucide-react';
import { useReveal } from '../../hooks/useReveal';
import RevealItem from '../RevealItem';
import { pastel } from '../../lib/palette';

const items = [
  {
    icon: Eye,
    title: 'Detección temprana',
    text: 'Visibiliza señales de convivencia antes de que escalen, con foco en lo observable.',
    tone: pastel.amber,
  },
  {
    icon: Route,
    title: 'Trazabilidad',
    text: 'Historial, evidencia y responsables en cada caso para una gestión transparente.',
    tone: pastel.violet,
  },
  {
    icon: Users2,
    title: 'Mejor coordinación',
    text: 'Roles, derivaciones y seguimiento alineados entre profesores, convivencia y dirección.',
    tone: pastel.teal,
  },
  {
    icon: Compass,
    title: 'Visión directiva',
    text: 'Información clara que apoya decisiones estratégicas del establecimiento.',
    tone: pastel.indigo,
  },
  {
    icon: Bell,
    title: 'Seguimiento oportuno',
    text: 'Alertas operativas y recordatorios para que ningún caso quede sin respuesta.',
    tone: pastel.coral,
  },
  {
    icon: TrendingUp,
    title: 'Implementación escalable',
    text: 'Base preparada para crecer por curso, ciclo o red de establecimientos.',
    tone: pastel.mint,
  },
];

const Beneficios = () => {
  const head = useReveal();
  return (
    <section id="beneficios" data-testid="beneficios-section" className="relative py-14 sm:py-20 bg-soft">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div ref={head.ref} className="reveal max-w-3xl">
          <div className="chip" data-testid="beneficios-chip">Beneficios para el establecimiento</div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-deep tracking-tight leading-tight">
            Una solución pensada para fortalecer la{' '}
            <span className="text-brand-violet">convivencia escolar</span> con información más útil y oportuna.
          </h2>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {items.map((it, idx) => (
            <RevealItem
              key={it.title}
              as="article"
              delay={idx * 60}
              className="group card-soft p-7 relative overflow-hidden"
              data-testid={`beneficio-${it.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {/* halo pastel decorativo en hover */}
              <span
                aria-hidden
                className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: it.tone.soft }}
              />
              <div className="relative">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: it.tone.bg, color: it.tone.fg }}
                >
                  <it.icon className="w-7 h-7" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-brand-deep">{it.title}</h3>
                <p className="mt-2 text-base text-brand-muted leading-relaxed">{it.text}</p>
              </div>
            </RevealItem>
          ))}
        </div>

        {/* Banner inferior */}
        <div className="mt-12 rounded-3xl bg-brand-deep text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8 relative overflow-hidden" data-testid="beneficios-banner">
          <div className="glow-violet" style={{ top: '-220px', right: '-160px', opacity: 0.35 }} aria-hidden />
          <span className="w-14 h-14 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-7 h-7 text-[#78A8FF]" />
          </span>
          <p className="text-lg sm:text-xl font-semibold leading-snug">
            Más prevención, <span className="text-[#78A8FF]">mejor gestión</span>, mayor resguardo institucional.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Beneficios;
